"use client";

import Image from "next/image";
import LastSevenDaysProgress from "../LastSevenDaysProgress/LastSevenDaysProgress";
import ResourcePopup from "../ResourcePopup/ResourcePopup";
import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import moment from "moment-timezone";

export default function Streak({
    session,
    showTipsOnTop = false,
    hoverRounded = "rounded-xl",
}) {
    const locale = useLocale();
    const t = useTranslations("layout");
    const [openStreakInfos, setOpenStreakInfos] = useState(false);

    // Compteurs blindés : quand /refresh-user échoue, la session retombe sur
    // l'instantané figé au login (mode `stale`), et celui d'une connexion
    // Google/Apple ne porte AUCUN champ de gamification. Un `.toLocaleString()`
    // sur undefined levait alors une exception en pleine phase de rendu, ce qui
    // emportait toute la page : ce widget est dans la barre du haut de chaque
    // écran. Un compteur décoratif ne doit jamais pouvoir faire ça.
    const streakCount = session?.user?.streakCount ?? 0;
    const maxStreakCount = session?.user?.maxStreakCount ?? 0;
    const shields = session?.user?.shields ?? 0;
    const activity = session?.user?.activity ?? {};
    const shieldedDates = session?.user?.shieldedDates ?? [];
    const timezone = session?.user?.timezone || moment.tz.guess();

    // Streak at risk: user has a streak but hasn't progressed today
    const todayDateStr = moment().tz(timezone).format("YYYY-MM-DD");
    const streakAtRisk = streakCount > 0 && !((activity[todayDateStr] || 0) > 0);

    return (
        <div
            className={`flex items-center gap-1.5 hover:background-blur dark:hover:bg-white/10 duration-100 cursor-pointer p-2 ${hoverRounded} relative`}
            onMouseOver={() => setOpenStreakInfos(true)}
            onMouseOut={() => setOpenStreakInfos(false)}
        >
            <Image
                src="/static/icons/streak.svg"
                width={15}
                height={15}
                alt=""
            />
            <div className="text-sm font-bold font-geistMono text-gray-800/70 dark:text-lightBlue tabular-nums">
                {streakCount.toLocaleString(locale)}
            </div>

            <ResourcePopup
                open={openStreakInfos}
                showTipsOnTop={showTipsOnTop}
                width="w-[400px]"
                icon={
                    <Image
                        src="/static/icons/streak.svg"
                        width={18}
                        height={18}
                        alt=""
                    />
                }
                title={t("Streak_title")}
                badge={
                    streakAtRisk ? (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-500/10">
                            <svg
                                className="w-3.5 h-3.5 text-red-400"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={1.5}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 8.5V12.5"
                                />
                                <path
                                    d="M12 16.5h.01"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                            <span className="text-xs font-semibold text-red-500 dark:text-red-400">
                                {t("Streak_at_risk")}
                            </span>
                        </div>
                    ) : null
                }
            >
                <div className="text-[#06050F]/60 dark:text-lightBlue font-geistSans text-sm mt-1">
                    {streakCount > 1
                        ? t("Streak_x_days", {
                              days: streakCount.toLocaleString(
                                  locale,
                              ),
                          })
                        : streakCount == 0
                        ? t("Streak_zero")
                        : t("Streak_one_day")}
                </div>
                <LastSevenDaysProgress
                    activity={activity}
                    userTimezone={timezone}
                    streakCount={streakCount}
                    shieldedDates={shieldedDates}
                />
                <div className="justify-between gap-4 flex mt-5 pt-5 text-sm font-bold text-[#05060F]/70 dark:text-lightBlue border-t border-gray-100 dark:border-grayDark-700/50">
                    <div className="flex flex-col gap-1.5 flex-1">
                        <span className="uppercase text-xs dark:text-white">
                            {t("Streak_longest")}
                        </span>
                        <div className="flex items-center gap-3">
                            <div>
                                <Image
                                    src="/static/icons/streak.svg"
                                    width={20}
                                    height={20}
                                    alt=""
                                />
                            </div>
                            {maxStreakCount.toLocaleString(locale)}{" "}
                            {maxStreakCount > 1
                                ? t("Streak_days")
                                : t("Streak_day")}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5 flex-1">
                        <span className="uppercase text-xs dark:text-white">
                            {t("Streak_shields")}
                        </span>
                        <div className="flex items-center gap-3">
                            <div>
                                <Image
                                    src="/static/icons/shield.svg"
                                    width={20}
                                    height={20}
                                    alt=""
                                />
                            </div>
                            {shields.toLocaleString(locale)}{" "}
                            {shields > 1
                                ? t("Streak_shields_plural")
                                : t("Streak_shields_singular")}
                        </div>
                    </div>
                </div>
            </ResourcePopup>
        </div>
    );
}
